import { useState } from 'react';

import { Button } from '@iwoca/orion';
import { Form, Formik, useField } from 'formik';
import { useNavigate } from 'react-router';

import { businessDetailsValidator } from './businessDetailTypeValidator';
import { CompanyInput } from '../../../../components/CompanyInput/CompanyInput';
import { Dropdown } from '../../../../components/Dropdown/Dropdown';
import { formatCurrency } from '../../../../components/Input/formatCurrency';
import { Input } from '../../../../components/Input/Input';
import { TappableTooltip } from '../../../../components/TappableTooltip/TappableTooltip';
import { Checkbox } from '../../../components/Checkbox/Checkbox';
import { InlineButton } from '../../../components/InlineButton/InlineButton';
import { WhiteLabelOverride } from '../../../components/WhiteLabelOverride/WhiteLabelOverride';
import { useSoleTraderPilot } from '../../../hooks/useSoletraderPilot';
import { buildQueryString } from '../../../utils/queryParams';
import { IndustriesModal } from '../../components/IndustriesModal/IndustriesModal';
import { RadioButton } from '../../components/RadioButton/RadioButton';
import { StepCard } from '../../components/StepCard/StepCard';
import { denormaliseDate } from '../../stateNormalisation';
import { BUSINESS_TYPE_OPTIONS } from '../BusinessType/BusinessType';
import { BUSINESS_DETAILS_STEP_ID, DIRECTOR_INFO_STEP_ID } from '../Steps';
import { useNavigateToStep } from '../useNavigateToStep';
import { useStepData } from '../useStepData';
import { useStepStatus } from '../useStepStatus';

export const BusinessDetails = () => {
  const { stepData, updateStepData, isUpdatingStepData } = useStepData(
    BUSINESS_DETAILS_STEP_ID,
  );

  const stepStatus = useStepStatus(BUSINESS_DETAILS_STEP_ID);
  const isActive = stepStatus === 'active';
  const isCompleted = stepStatus === 'complete';
  const { eligible: isSoleTraderPilot } = useSoleTraderPilot();

  const goToCurrentStep = useNavigateToStep(BUSINESS_DETAILS_STEP_ID);
  const goToNextStep = useNavigateToStep(DIRECTOR_INFO_STEP_ID);

  const navigate = useNavigate();
  const queryString = buildQueryString();

  const handleSubmit = async (values: typeof stepData) => {
    await updateStepData(values);

    if (values.isDirector === 'false')
      return navigate(`/pay/signup/speak-to-director${queryString}`);

    goToNextStep();
  };

  return (
    <StepCard title="2. Your business info" isCompleted={isCompleted}>
      <Formik
        initialValues={stepData}
        onSubmit={async (values) => {
          await handleSubmit(values);
        }}
        enableReinitialize={!isUpdatingStepData}
        validationSchema={businessDetailsValidator}
      >
        {({ values }) => {
          const isSoleTrader = values.companyType === 'sole_trader';
          const showSoleTraderFlow = isSoleTrader && isSoleTraderPilot;

          if (isActive) {
            return (
              <Form
                className="relative flex flex-col gap-xl"
                autoComplete="off"
              >
                <CompanyInput />
                {!isSoleTrader && <AreYouADirector />}
                {values.isDirector !== 'false' && (
                  <Input
                    className="w-full max-w-[400px]"
                    name="last12MonthsTurnover"
                    label={
                      <span className="flex items-center gap-xs">
                        Last 12 months turnover
                        <TappableTooltip
                          className="w-[200px]"
                          content="If you have been trading for less than a year, enter your total turnover to date."
                        ></TappableTooltip>
                      </span>
                    }
                    labelDescriptionText="Enter the full amount. e.g. £30,000"
                    placeholder="Enter amount..."
                    formatter={formatCurrency}
                    inputMode="numeric"
                  />
                )}
                {showSoleTraderFlow && <SoleTraderIndustryQuestion />}
                <WhiteLabelOverride
                  componentType="button"
                  disabled={isUpdatingStepData}
                >
                  <Button
                    disabled={isUpdatingStepData}
                    loading={isUpdatingStepData}
                    className="w-fit sm:w-full"
                  >
                    Continue to your details
                  </Button>
                </WhiteLabelOverride>
              </Form>
            );
          }

          if (isCompleted) {
            return (
              <div className="fs-mask font-reg text-black-90 [&_p]:text-[14px] [&_p]:leading-[20px]">
                {showSoleTraderFlow ? (
                  <>
                    <p className="m-0">{values.tradingName}</p>
                    <p className="m-0">
                      {
                        BUSINESS_TYPE_OPTIONS.find(
                          ({ value }) => value === values.companyType,
                        )?.label
                      }
                    </p>
                    {values.tradingStartDate && (
                      <p className="m-0">
                        Trading start date:{' '}
                        {getFormattedDate(values.tradingStartDate)}
                      </p>
                    )}
                    <p className="mb-l mt-[18px]">
                      Last 12 months turnover: £{values.last12MonthsTurnover}
                    </p>
                  </>
                ) : (
                  <>
                    <p className="m-0">{values.companyName}</p>
                    <p className="m-0">{values.companyNumber}</p>
                    <p className="m-0">
                      {
                        BUSINESS_TYPE_OPTIONS.find(
                          ({ value }) => value === values.companyType,
                        )?.label
                      }
                    </p>
                    {values.tradingStartDate && (
                      <p className="m-0">
                        Trading start date:{' '}
                        {getFormattedDate(values.tradingStartDate)}
                      </p>
                    )}
                    <p className="mb-l mt-[18px]">
                      Last 12 months turnover: £{values.last12MonthsTurnover}
                    </p>
                  </>
                )}
                <InlineButton onClick={goToCurrentStep}>Edit</InlineButton>
              </div>
            );
          }
          return null;
        }}
      </Formik>
    </StepCard>
  );
};

const AreYouADirector = () => {
  const [field] = useField('isDirector');

  return (
    <div className="flex flex-col gap-xl">
      <RadioButton
        labelText="Are you a director of the company?"
        name="isDirector"
        options={[
          { label: 'Yes', value: 'true' },
          { label: 'No', value: 'false' },
        ]}
        className="max-w-[400px]"
        compact={true}
      />
      {field.value === 'false' && (
        <>
          <Dropdown name="title" labelText="Title" className="max-w-[400px]">
            <option value=""></option>
            <option value="mr">Mr</option>
            <option value="ms">Ms</option>
            <option value="mrs">Mrs</option>
            <option value="miss">Miss</option>
            <option value="sir">Sir</option>
            <option value="professor">Professor</option>
            <option value="doctor">Doctor</option>
            <option value="lord">Lord</option>
            <option value="lady">Lady</option>
            <option value="baron">Baron</option>
            <option value="baroness">Baroness</option>
            <option value="rev">Reverend</option>
          </Dropdown>
          <Input
            type="text"
            label="First Name"
            labelDescriptionText=""
            name="firstName"
            className="w-full max-w-[400px]"
          />
          <Input
            type="text"
            label="Last Name"
            labelDescriptionText=""
            name="lastName"
            className="w-full max-w-[400px]"
          />
        </>
      )}
    </div>
  );
};

const SoleTraderIndustryQuestion = () => {
  const [showRiskyCompaniesModalOpen, setShowRiskyCompaniesModalOpen] =
    useState(false);

  const onClickModal = () => {
    setShowRiskyCompaniesModalOpen(!showRiskyCompaniesModalOpen);
  };

  return (
    <>
      <IndustriesModal
        onClickModal={onClickModal}
        showRiskyCompaniesModalOpen={showRiskyCompaniesModalOpen}
      />
      <Checkbox
        name="agreeSoleTraderPilotDeclaration"
        wrapperClassName="flex [&>input]:shrink-0"
        labelText={
          <span data-testid="agreeSoleTraderPilotDeclaration">
            I confirm that I will use these funds for business purposes & the
            funds will not be used to participate in any of the following
            industries:{' '}
            <InlineButton onClick={onClickModal}>See industries</InlineButton>
          </span>
        }
      />
    </>
  );
};

export const getFormattedDate = (dateString: string | null = '') => {
  const date = new Date(dateString ? denormaliseDate(dateString) : '');
  const formatter = new Intl.DateTimeFormat('en-GB', {
    year: 'numeric',
    month: 'short',
  });
  return formatter.format(date);
};
